// @flow
import * as R from "ramda"

import { SET_ANCILLARIES, ADD_ANCILLARY, REMOVE_ANCILLARY, SET_ANCILLARY_STATUS } from "./actions"
import type {
  setAncillaryAction,
  addRemoveAncillaryAction,
  ancillariesLoadingAction,
} from "./actions"
import type { AncillaryStandardSegment, CoverMoreSegment, FastTrackSegment } from "./types"
import ANCILLARY_SERVICES from "./consts"

export type AncillaryStateData =
  | ?AncillaryStandardSegment
  | ?(AncillaryStandardSegment[])
  | ?(CoverMoreSegment[])
  | ?(FastTrackSegment[])

export type AncillariesStatus = "valid" | "loading"

export type AncillaryState = {
  name: string,
  service: string,
  type: ?number,
  data: AncillaryStateData,
  isAdded: boolean,
  forPassengers: Array<number>,
  status: AncillariesStatus,
}

const setAncillariesStatus = (
  state: AncillaryState,
  action: ancillariesLoadingAction,
  currentService: string,
) => {
  const { services, isLoading } = action.payload
  const shouldChange = services.includes(currentService)

  if (!shouldChange) {
    return state
  }

  return {
    ...state,
    status: isLoading ? "loading" : "valid",
  }
}

/**
 * Factory for Ancillaries3rd service reducer. For registered services It will save raw data, that
 * it got from server, to state. All additional transformations are done by selectors
 * @param {string} service - name of the service you are registering, needs to equal to one of
 * keys defines in ANCILLARY_SERVICES
 */
const createAncillaryReducer = (service: string) => (
  state: AncillaryState = {
    name: "",
    service: ANCILLARY_SERVICES[service],
    type: null,
    isAdded: false,
    forPassengers: [],
    data: null,
    status: "valid",
  },
  action: setAncillaryAction | addRemoveAncillaryAction,
) => {
  const serviceKey = ANCILLARY_SERVICES[service]
  switch (action.type) {
    case SET_ANCILLARIES:
      if (R.prop(serviceKey, action.payload)) {
        return { ...state, ...action.payload[serviceKey] }
      }
      return state
    case ADD_ANCILLARY:
      if (action.meta.service === service) {
        return {
          ...state,
          isAdded: true,
          // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
          forPassengers: R.uniq([...state.forPassengers, ...action.meta.forPassengers]),
        }
      }
      return state
    case REMOVE_ANCILLARY:
      if (action.meta.service === service) {
        // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
        const updatedPassengers = R.without(action.meta.forPassengers, state.forPassengers)
        return {
          ...state,
          isAdded: action.meta.forPassengers.length ? updatedPassengers.length !== 0 : false,
          forPassengers: action.meta.forPassengers.length ? updatedPassengers : [],
        }
      }
      return state
    case SET_ANCILLARY_STATUS:
      return setAncillariesStatus(state, action, service)
    default:
      return state
  }
}

export default createAncillaryReducer
